import React, { useState } from 'react';
import {
  AgenticCompetency,
  AgenticCompetencyType,
  AgenticConfig,
  AgenticDraft,
} from '../../../../../utils/types/agentic_assessment_types';
import { COMPETENCY_TYPES, LEVEL_HINTS, LEVEL_NAMES } from '../constants';

interface BlueprintEditorProps {
  draft: AgenticDraft;
  config: AgenticConfig;
  readOnly: boolean;
  onChange: (draft: AgenticDraft) => void;
  onConfigChange: (config: AgenticConfig) => void;
}

const inputClass =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 disabled:bg-slate-50 disabled:text-slate-500';

const LEVELS = [1, 2, 3, 4, 5] as const;
const anchorKey = (level: number) => `l${level}` as 'l1' | 'l2' | 'l3' | 'l4' | 'l5';

export const BlueprintEditor: React.FC<BlueprintEditorProps> = ({ draft, config, readOnly, onChange, onConfigChange }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const totalWeight = draft.competencies.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);

  const updateCompetency = (index: number, patch: Partial<AgenticCompetency>) => {
    const competencies = draft.competencies.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange({ ...draft, competencies });
  };

  const updateAnchor = (index: number, level: number, text: string) => {
    const current = draft.competencies[index].anchors || { l1: '', l2: '', l3: '', l4: '', l5: '' };
    updateCompetency(index, { anchors: { ...current, [anchorKey(level)]: text } });
  };

  const addCompetency = () => {
    onChange({
      ...draft,
      competencies: [
        ...draft.competencies,
        { name: '', type: 'judgment', weight: 10, targetLevel: 3, mustHave: false, assessableInText: true },
      ],
    });
    setOpenIndex(draft.competencies.length);
  };

  const removeCompetency = (index: number) => {
    onChange({ ...draft, competencies: draft.competencies.filter((_, i) => i !== index) });
    setOpenIndex(null);
  };

  return (
    <div className='flex flex-col gap-5'>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
        <label className='flex flex-col gap-1 md:col-span-1'>
          <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Role title</span>
          <input
              className={inputClass}
              value={draft.roleTitle}
              disabled={readOnly}
              onChange={(e) => onChange({ ...draft, roleTitle: e.target.value })}
          />
        </label>
        <label className='flex flex-col gap-1 md:col-span-2'>
          <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Role summary</span>
          <input
              className={inputClass}
              value={draft.roleSummary}
              disabled={readOnly}
              onChange={(e) => onChange({ ...draft, roleSummary: e.target.value })}
          />
        </label>
      </div>

      <div className='flex flex-wrap items-end gap-4 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3'>
        <label className='flex flex-col gap-1'>
          <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Questions per candidate</span>
          <input
              type='number'
              min={5}
              max={25}
              className={`${inputClass} w-28`}
              value={config.maxQuestions}
              disabled={readOnly}
              onChange={(e) => onConfigChange({ ...config, maxQuestions: Number(e.target.value) })}
          />
        </label>
        <label className='flex flex-col gap-1'>
          <span className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Max per competency</span>
          <input
              type='number'
              min={1}
              max={5}
              className={`${inputClass} w-28`}
              value={config.maxProbesPerCompetency}
              disabled={readOnly}
              onChange={(e) => onConfigChange({ ...config, maxProbesPerCompetency: Number(e.target.value) })}
          />
        </label>
        <p className='max-w-md text-xs text-slate-500'>
          Each answer moves the next question one level up or down until the candidate&apos;s level on each competency is pinned down.
          Weights total <span className='font-semibold tabular-nums text-slate-700'>{ totalWeight }</span>.
        </p>
      </div>

      <div className='overflow-hidden rounded-xl border border-slate-200'>
        <div className='grid grid-cols-[minmax(0,1fr)_120px_80px_150px_90px_32px] gap-3 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500'>
          <span>Competency</span>
          <span>Type</span>
          <span>Weight</span>
          <span>Target level</span>
          <span>Must-have</span>
          <span />
        </div>
        { draft.competencies.map((comp, index) => {
          const open = openIndex === index;
          const incomplete = !comp.description || !comp.anchors;
          return (
            <div key={comp.competencyId || `new-${index}`} className='border-t border-slate-100'>
              <div className='grid grid-cols-[minmax(0,1fr)_120px_80px_150px_90px_32px] items-center gap-3 px-4 py-2'>
                <div className='flex min-w-0 items-center gap-2'>
                  <button
                      type='button'
                      onClick={() => setOpenIndex(open ? null : index)}
                      className='text-slate-400 hover:text-slate-700'
                      aria-label={open ? 'Hide rubric' : 'Show rubric'}
                  >
                    { open ? '▾' : '▸' }
                  </button>
                  <input
                      className={inputClass}
                      placeholder='Competency name'
                      value={comp.name}
                      disabled={readOnly}
                      onChange={(e) => updateCompetency(index, { name: e.target.value })}
                  />
                  { incomplete && (
                    <span className='whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700'>
                      Rubric written on save
                    </span>
                  ) }
                  { !comp.assessableInText && (
                    <span className='whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600'>
                      In-person only
                    </span>
                  ) }
                </div>
                <select
                    className={inputClass}
                    value={comp.type}
                    disabled={readOnly}
                    onChange={(e) => updateCompetency(index, { type: e.target.value as AgenticCompetencyType })}
                >
                  { COMPETENCY_TYPES.map((t) => <option key={t.value} value={t.value}>{ t.label }</option>) }
                </select>
                <input
                    type='number'
                    min={1}
                    className={inputClass}
                    value={comp.weight}
                    disabled={readOnly}
                    onChange={(e) => updateCompetency(index, { weight: Number(e.target.value) })}
                />
                <select
                    className={inputClass}
                    value={comp.targetLevel}
                    disabled={readOnly}
                    onChange={(e) => updateCompetency(index, { targetLevel: Number(e.target.value) })}
                >
                  { LEVELS.map((l) => <option key={l} value={l}>L{ l } { LEVEL_NAMES[l] }</option>) }
                </select>
                <input
                    type='checkbox'
                    className='h-4 w-4 accent-emerald-600'
                    checked={comp.mustHave}
                    disabled={readOnly}
                    onChange={(e) => updateCompetency(index, { mustHave: e.target.checked })}
                />
                { !readOnly ? (
                  <button
                      type='button'
                      onClick={() => removeCompetency(index)}
                      className='text-slate-300 hover:text-rose-600'
                      aria-label={`Remove ${comp.name || 'competency'}`}
                  >
                    ✕
                  </button>
                ) : <span /> }
              </div>

              { open && (
                <div className='flex flex-col gap-3 bg-slate-50/50 px-10 pb-4 pt-1'>
                  <label className='flex flex-col gap-1'>
                    <span className='text-xs font-semibold text-slate-500'>Description</span>
                    <textarea
                        rows={2}
                        className={inputClass}
                        placeholder='Leave empty to have it written for you'
                        value={comp.description || ''}
                        disabled={readOnly}
                        onChange={(e) => updateCompetency(index, { description: e.target.value })}
                    />
                  </label>
                  <label className='inline-flex items-center gap-2 text-sm text-slate-600'>
                    <input
                        type='checkbox'
                        className='h-4 w-4 accent-emerald-600'
                        checked={comp.assessableInText}
                        disabled={readOnly}
                        onChange={(e) => updateCompetency(index, { assessableInText: e.target.checked })}
                    />
                    Can be assessed from written answers (untick for hands-on skills to check in person)
                  </label>
                  <div className='flex flex-col gap-2'>
                    <span className='text-xs font-semibold text-slate-500'>What an answer shows at each level</span>
                    { LEVELS.map((level) => (
                      <div key={level} className='grid grid-cols-[150px_minmax(0,1fr)] items-start gap-3'>
                        <div className='pt-2'>
                          <div className='text-xs font-semibold text-slate-700'>L{ level } { LEVEL_NAMES[level] }</div>
                          <div className='text-[11px] leading-snug text-slate-400'>{ LEVEL_HINTS[level] }</div>
                        </div>
                        <textarea
                            rows={2}
                            className={inputClass}
                            placeholder={comp.anchors ? '' : 'Written for you on save'}
                            value={comp.anchors ? comp.anchors[anchorKey(level)] : ''}
                            disabled={readOnly}
                            onChange={(e) => updateAnchor(index, level, e.target.value)}
                        />
                      </div>
                    )) }
                  </div>
                </div>
              ) }
            </div>
          );
        }) }
        { !readOnly && (
          <button
              type='button'
              onClick={addCompetency}
              className='w-full border-t border-dashed border-slate-200 px-4 py-2 text-left text-sm font-semibold text-emerald-700 hover:bg-emerald-50/50'
          >
            + Add competency
          </button>
        ) }
      </div>
    </div>
  );
};
